import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Plus, Pencil, Trash2, Package, FileText, Mail, DollarSign, Users,
  ShoppingBag, Tag, AlertTriangle, BarChart3, X, Upload, TrendingUp,
  Percent, BookOpen, Palette, Image as ImageIcon, Save, Eye, Warehouse,
  ArrowUp, ArrowDown, Bell, Type, Loader2, Minus
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHART_COLORS = ['#9C6ADE', '#d4e8a0', '#7B4FB0', '#8B9A3E', '#F0E8FF', '#EFF5DD'];

const CATEGORIES = [
  { value: 'limpiadores', label: 'Limpiadores' },
  { value: 'toners', label: 'Toners' },
  { value: 'serums', label: 'Serums' },
  { value: 'protectores_solares', label: 'Protectores Solares' },
  { value: 'piel_grasa', label: 'Piel Grasa' },
  { value: 'piel_sensible', label: 'Piel Sensible' },
  { value: 'blotting_papers', label: 'Blotting Papers' },
  { value: 'kits', label: 'Kits' },
];

const FONTS = [
  'Outfit', 'Inter', 'Poppins', 'Montserrat', 'Playfair Display', 'DM Sans',
  'Nunito', 'Lato', 'Raleway', 'Cormorant Garamond', 'Source Sans 3',
];

/* ─── Stat Card ─── */
function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-[var(--sage)]/15" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-xl font-bold text-[var(--dark)]">{value}</p>
          <p className="text-[10px] text-[var(--dark)]/40">{label}</p>
          {sub && <p className="text-[10px] text-[var(--violet)]">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

/* ─── Image Manager ─── */
function ImageManager({ images = [], onChange, token, single = false }) {
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API}/upload`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        newUrls.push(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      } catch { /* skip failed */ }
    }
    if (single) onChange(newUrls.slice(0, 1));
    else onChange([...images, ...newUrls]);
    setUploading(false);
    e.target.value = '';
  };

  const addUrl = () => {
    if (!imageUrl.trim()) return;
    if (single) onChange([imageUrl.trim()]);
    else onChange([...images, imageUrl.trim()]);
    setImageUrl('');
  };

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));
  const moveUp = (idx) => { if (idx === 0) return; const a = [...images]; [a[idx-1], a[idx]] = [a[idx], a[idx-1]]; onChange(a); };
  const moveDown = (idx) => { if (idx >= images.length - 1) return; const a = [...images]; [a[idx], a[idx+1]] = [a[idx+1], a[idx]]; onChange(a); };

  return (
    <div className="space-y-2">
      <Label className="text-xs">{single ? 'Imagen' : `Imagenes (${images.length})`}</Label>
      {images.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {images.map((img, i) => (
            <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border group">
              <img src={img} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                {!single && images.length > 1 && (
                  <>
                    <button onClick={() => moveUp(i)} className="w-5 h-5 rounded-full bg-white flex items-center justify-center"><ArrowUp className="h-3 w-3" /></button>
                    <button onClick={() => moveDown(i)} className="w-5 h-5 rounded-full bg-white flex items-center justify-center"><ArrowDown className="h-3 w-3" /></button>
                  </>
                )}
                <button onClick={() => remove(i)} className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center" data-testid={`remove-image-${i}`}><X className="h-3 w-3" /></button>
              </div>
              {i === 0 && !single && <span className="absolute bottom-0 left-0 right-0 bg-[var(--violet)] text-white text-[8px] text-center py-0.5">Principal</span>}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="Pegar URL de imagen" className="h-8 text-xs flex-1"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }} />
        <Button size="sm" variant="outline" onClick={addUrl} className="h-8 text-xs">URL</Button>
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-[var(--violet)] hover:underline" data-testid="upload-image-btn">
        <Upload className="h-3 w-3" /> {uploading ? 'Subiendo...' : (single ? 'Subir imagen' : 'Subir imagenes')}
        <input type="file" accept="image/*" multiple={!single} onChange={handleUpload} className="hidden" />
      </label>
    </div>
  );
}

/* ─── Tag Input Helper ─── */
function TagInput({ label, values = [], onChange, placeholder }) {
  const [input, setInput] = useState('');
  const add = () => { if (input.trim()) { onChange([...values, input.trim()]); setInput(''); } };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((v, i) => (
            <Badge key={i} variant="outline" className="text-[10px] cursor-pointer hover:bg-red-50 hover:text-red-500 pr-1" onClick={() => onChange(values.filter((_, j) => j !== i))}>
              {v} <X className="h-2.5 w-2.5 ml-0.5" />
            </Badge>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder={placeholder || `Agregar ${label.toLowerCase()}`} className="h-8 text-xs flex-1" />
        <Button size="sm" variant="outline" onClick={add} className="h-8 text-xs px-2"><Plus className="h-3 w-3" /></Button>
      </div>
    </div>
  );
}

/* ─── Product Form ─── */
function ProductForm({ product, onSave, onClose, token }) {
  const [form, setForm] = useState(product || {
    name: '', brand: '', description: '', short_description: '', price: 0, discount_price: null,
    category: 'serums', skin_types: [], benefits: [], ingredients: [], tags: [],
    how_to_use: '', images: [], featured: false, active: true, stock: 50, low_stock_threshold: 5
  });
  const [saving, setSaving] = useState(false);
  const f = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const handleSave = async () => {
    if (!form.name?.trim()) { alert('El nombre es requerido'); return; }
    if (!form.price || parseFloat(form.price) <= 0) { alert('El precio debe ser mayor a 0'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 0, low_stock_threshold: parseInt(form.low_stock_threshold) || 5 };
      payload.discount_price = form.discount_price !== null && form.discount_price !== '' ? parseFloat(form.discount_price) : null;
      await onSave(payload);
      onClose();
    } catch (err) { alert(err.response?.data?.detail || 'Error al guardar producto'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" data-testid="product-form">
      {/* Basic Info */}
      <div className="bg-[var(--cream)]/50 rounded-xl p-4 space-y-3">
        <p className="text-[10px] font-bold text-[var(--dark)]/30 uppercase tracking-wider">Informacion Basica</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Nombre *</Label><Input value={form.name} onChange={e => f('name', e.target.value)} className="h-9 text-sm" data-testid="product-form-name" /></div>
          <div className="space-y-1"><Label className="text-xs">Marca</Label><Input value={form.brand} onChange={e => f('brand', e.target.value)} className="h-9 text-sm" /></div>
        </div>
        <div className="space-y-1"><Label className="text-xs">Descripcion Corta</Label><Input value={form.short_description} onChange={e => f('short_description', e.target.value)} className="h-9 text-sm" placeholder="Resumen breve del producto" /></div>
        <div className="space-y-1"><Label className="text-xs">Descripcion Larga</Label><Textarea value={form.description} onChange={e => f('description', e.target.value)} className="text-sm min-h-[80px]" /></div>
      </div>

      {/* Pricing & Stock */}
      <div className="bg-[var(--cream)]/50 rounded-xl p-4 space-y-3">
        <p className="text-[10px] font-bold text-[var(--dark)]/30 uppercase tracking-wider">Precio e Inventario</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="space-y-1"><Label className="text-xs">Precio Original ($) *</Label><Input type="number" step="0.01" value={form.price} onChange={e => f('price', e.target.value)} className="h-9 text-sm" data-testid="product-form-price" /></div>
          <div className="space-y-1"><Label className="text-xs">Precio Descuento ($)</Label><Input type="number" step="0.01" value={form.discount_price || ''} onChange={e => f('discount_price', e.target.value || null)} className="h-9 text-sm" placeholder="Opcional" /></div>
          <div className="space-y-1"><Label className="text-xs">Stock</Label><Input type="number" value={form.stock} onChange={e => f('stock', e.target.value)} className="h-9 text-sm" data-testid="product-form-stock" /></div>
          <div className="space-y-1"><Label className="text-xs">Alerta Stock Min</Label><Input type="number" value={form.low_stock_threshold} onChange={e => f('low_stock_threshold', e.target.value)} className="h-9 text-sm" /></div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Categoria</Label>
          <Select value={form.category} onValueChange={v => f('category', v)}>
            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Images */}
      <div className="bg-[var(--cream)]/50 rounded-xl p-4">
        <p className="text-[10px] font-bold text-[var(--dark)]/30 uppercase tracking-wider mb-2">Galeria de Imagenes</p>
        <ImageManager images={form.images || []} onChange={imgs => f('images', imgs)} token={token} />
      </div>

      {/* Tags & Attributes */}
      <div className="bg-[var(--cream)]/50 rounded-xl p-4 space-y-3">
        <p className="text-[10px] font-bold text-[var(--dark)]/30 uppercase tracking-wider">Atributos</p>
        <TagInput label="Tags" values={form.tags || []} onChange={v => f('tags', v)} placeholder="ej: vegano, sin fragancia" />
        <TagInput label="Beneficios" values={form.benefits || []} onChange={v => f('benefits', v)} placeholder="ej: hidratante, calmante" />
        <TagInput label="Ingredientes" values={form.ingredients || []} onChange={v => f('ingredients', v)} placeholder="ej: niacinamide, centella" />
        <TagInput label="Tipos de Piel" values={form.skin_types || []} onChange={v => f('skin_types', v)} placeholder="ej: grasa, sensible, todos" />
      </div>

      {/* How to use */}
      <div className="space-y-1"><Label className="text-xs">Como usarlo</Label><Textarea value={form.how_to_use} onChange={e => f('how_to_use', e.target.value)} className="text-sm min-h-[60px]" /></div>

      {/* Toggles */}
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={v => f('featured', v)} /><Label className="text-xs">Destacado</Label></div>
        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => f('active', v)} /><Label className="text-xs">Activo</Label></div>
      </div>

      <div className="flex gap-3 pt-2 sticky bottom-0 bg-white py-3 border-t border-[var(--sage)]/10">
        <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[var(--violet)] hover:bg-[var(--violet-dark)] text-white h-10" data-testid="product-form-save">
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</> : <><Save className="h-4 w-4 mr-2" /> Guardar Producto</>}
        </Button>
        <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancelar</Button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════ */
/* ═══           ADMIN PAGE                ═══ */
/* ════════════════════════════════════════════ */

export default function AdminPage() {
  const { token, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [education, setEducation] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [chartData, setChartData] = useState({ monthly: [], by_category: {} });
  const [analytics, setAnalytics] = useState(null);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [gaId, setGaId] = useState('');

  // Product
  const [editProduct, setEditProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [productFilterCat, setProductFilterCat] = useState('all');
  const [productSearch, setProductSearch] = useState('');

  // Blog
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editBlog, setEditBlog] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: '', slug: '', content: '', excerpt: '', image: '', category: '', published: false });

  // Education
  const [showEduForm, setShowEduForm] = useState(false);
  const [editEdu, setEditEdu] = useState(null);
  const [eduForm, setEduForm] = useState({ title: '', slug: '', content: '', image: '', order: 0, published: false });

  // Coupons
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({ code: '', discount_type: 'percentage', discount_value: 10, min_purchase: 0, max_uses: 100, active: true, expires_at: '' });

  // Design
  const [designForm, setDesignForm] = useState(null);
  const [designSaving, setDesignSaving] = useState(false);

  // Inventory
  const [stockEditId, setStockEditId] = useState(null);
  const [stockEditVal, setStockEditVal] = useState('');
  const [inventoryFilter, setInventoryFilter] = useState('all');

  const [filterCat, setFilterCat] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(null);

  const h = { headers: { Authorization: `Bearer ${token}` } };

  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/admin/stats`, h),
        axios.get(`${API}/admin/products`, h),
        axios.get(`${API}/admin/blog`, h),
        axios.get(`${API}/admin/education`, h),
        axios.get(`${API}/contact`, h),
        axios.get(`${API}/orders/all`, h),
        axios.get(`${API}/coupons`, h),
        axios.get(`${API}/admin/sales-chart`, h),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/analytics`, h),
      ]);
      const get = (i) => results[i].status === 'fulfilled' ? results[i].value.data : null;
      if (get(0)) setStats(get(0));
      if (get(1)) setProducts(get(1));
      if (get(2)) setBlogPosts(get(2));
      if (get(3)) setEducation(get(3));
      if (get(4)) setContacts(get(4));
      if (get(5)) setOrders(get(5));
      if (get(6)) setCoupons(get(6));
      if (get(7)) setChartData(get(7));
      if (get(8) && !designForm) setDesignForm(get(8));
      if (get(9)) setAnalytics(get(9));
    } catch (err) { console.error('Load error:', err); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  if (authLoading) return (
    <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
      <div className="text-center space-y-3">
        <Loader2 className="h-8 w-8 text-[var(--violet)] animate-spin mx-auto" />
        <p className="text-sm text-[var(--dark)]/40">Cargando panel...</p>
      </div>
    </div>
  );
  if (!isAdmin) return <Navigate to="/" />;

  /* ─── Product Actions ─── */
  const saveProduct = async (data) => {
    if (editProduct?.id) await axios.put(`${API}/products/${editProduct.id}`, data, h);
    else await axios.post(`${API}/products`, data, h);
    loadData();
  };
  const deleteProduct = async (id) => {
    if (!window.confirm('Eliminar este producto?')) return;
    setDeleteLoading(id);
    try { await axios.delete(`${API}/products/${id}`, h); setProducts(p => p.filter(x => x.id !== id)); }
    catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    setDeleteLoading(null);
  };

  /* ─── Blog Actions ─── */
  const saveBlog = async () => {
    const data = { ...blogForm }; if (!data.slug) data.slug = data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    try {
      if (editBlog?.id) await axios.put(`${API}/blog/${editBlog.id}`, data, h);
      else await axios.post(`${API}/blog`, data, h);
      setShowBlogForm(false); setEditBlog(null); setBlogForm({ title: '', slug: '', content: '', excerpt: '', image: '', category: '', published: false }); loadData();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };
  const deleteBlog = async (id) => {
    if (!window.confirm('Eliminar?')) return; setDeleteLoading(id);
    try { await axios.delete(`${API}/blog/${id}`, h); setBlogPosts(p => p.filter(x => x.id !== id)); }
    catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    setDeleteLoading(null);
  };

  /* ─── Education Actions ─── */
  const saveEducation = async () => {
    const data = { ...eduForm, order: parseInt(eduForm.order) || 0 }; if (!data.slug) data.slug = data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    try {
      if (editEdu?.id) await axios.put(`${API}/education/${editEdu.id}`, data, h);
      else await axios.post(`${API}/education`, data, h);
      setShowEduForm(false); setEditEdu(null); setEduForm({ title: '', slug: '', content: '', image: '', order: 0, published: false }); loadData();
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };
  const deleteEducation = async (id) => {
    if (!window.confirm('Eliminar?')) return; setDeleteLoading(id);
    try { await axios.delete(`${API}/education/${id}`, h); setEducation(p => p.filter(x => x.id !== id)); }
    catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    setDeleteLoading(null);
  };

  /* ─── Coupon Actions ─── */
  const saveCoupon = async () => {
    const data = { ...couponForm, discount_value: parseFloat(couponForm.discount_value), min_purchase: parseFloat(couponForm.min_purchase), max_uses: parseInt(couponForm.max_uses) };
    if (!data.expires_at) delete data.expires_at;
    try {
      if (editCoupon?.id) await axios.put(`${API}/coupons/${editCoupon.id}`, data, h);
      else await axios.post(`${API}/coupons`, data, h);
      setShowCouponForm(false); setEditCoupon(null); setCouponForm({ code: '', discount_type: 'percentage', discount_value: 10, min_purchase: 0, max_uses: 100, active: true, expires_at: '' }); loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };
  const deleteCoupon = async (id) => {
    if (!window.confirm('Eliminar?')) return; setDeleteLoading(id);
    try { await axios.delete(`${API}/coupons/${id}`, h); setCoupons(p => p.filter(x => x.id !== id)); }
    catch (err) { alert('Error'); }
    setDeleteLoading(null);
  };

  /* ─── Inventory Actions ─── */
  const updateStock = async (productId, action, amount) => {
    try {
      const res = await axios.put(`${API}/admin/inventory/${productId}`, { action, amount: parseInt(amount) }, h);
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, stock: res.data.stock } : p));
      setStockEditId(null);
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  /* ─── Design Actions ─── */
  const saveDesign = async () => {
    setDesignSaving(true);
    try { await axios.put(`${API}/settings`, designForm, h); loadData(); alert('Diseno guardado! Recarga la pagina para ver los cambios.'); }
    catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    setDesignSaving(false);
  };

  /* ─── OMS: Order Status Update ─── */
  const updateOrderStatus = async (orderId, newStatus, extra = {}) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus, ...extra }, h);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (err) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
  };

  /* ─── Computed ─── */
  const filteredProducts = products.filter(p => {
    const matchCat = productFilterCat === 'all' || p.category === productFilterCat;
    const matchSearch = !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.brand?.toLowerCase().includes(productSearch.toLowerCase());
    return matchCat && matchSearch;
  });

  const inventoryProducts = products.filter(p => {
    if (inventoryFilter === 'low') return (p.stock || 0) <= (p.low_stock_threshold || 5) && (p.stock || 0) > 0;
    if (inventoryFilter === 'out') return (p.stock || 0) <= 0;
    if (inventoryFilter === 'ok') return (p.stock || 0) > (p.low_stock_threshold || 5);
    return true;
  }).sort((a, b) => (a.stock || 0) - (b.stock || 0));

  const lowStockProducts = products.filter(p => (p.stock || 0) <= (p.low_stock_threshold || 5) && (p.stock || 0) > 0);
  const outOfStockProducts = products.filter(p => (p.stock || 0) <= 0);

  const filteredOrders = orders.filter(o => {
    const matchCat = !filterCat || filterCat === 'all' || o.items?.some(i => { const prod = products.find(p => p.id === i.product_id); return prod?.category === filterCat; });
    const matchStatus = orderStatusFilter === 'all' || o.status === orderStatusFilter;
    return matchCat && matchStatus;
  });
  const categoryPieData = Object.entries(chartData.by_category || {}).map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }));

  const STATUS_LABELS = { pending: 'Pendiente', paid: 'Pagado', shipped: 'Enviado', completed: 'Completado', cancelled: 'Cancelado' };
  const STATUS_COLORS = { pending: 'bg-yellow-50 text-yellow-700', paid: 'bg-blue-50 text-blue-700', shipped: 'bg-purple-50 text-purple-700', completed: 'bg-green-50 text-green-700', cancelled: 'bg-red-50 text-red-500' };
  const STATUS_NEXT = { pending: 'paid', paid: 'shipped', shipped: 'completed' };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="admin-page">
      <div className="bg-white border-b border-[var(--sage)]/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <h1 className="text-xl font-bold text-[var(--dark)]">Panel de Administracion</h1>
          <p className="text-xs text-[var(--dark)]/40">Gestion completa de tu tienda Aura K Beauty</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
          <StatCard icon={DollarSign} label="Ingresos" value={`$${stats.total_revenue?.toFixed(2) || '0.00'}`} color="bg-green-50 text-green-600" />
          <StatCard icon={ShoppingBag} label="Pedidos" value={stats.orders || 0} color="bg-[var(--violet-light)] text-[var(--violet)]" />
          <StatCard icon={Package} label="Productos" value={stats.products || 0} color="bg-[var(--sage-light)] text-[var(--sage-dark)]" />
          <StatCard icon={Users} label="Usuarios" value={stats.users || 0} color="bg-blue-50 text-blue-600" />
          <StatCard icon={Tag} label="Cupones" value={stats.active_coupons || 0} color="bg-purple-50 text-purple-600" />
          <StatCard icon={AlertTriangle} label="Stock Bajo" value={stats.low_stock || 0} color="bg-orange-50 text-orange-600" sub={stats.low_stock > 0 ? 'Revisar inventario' : ''} />
          <StatCard icon={Mail} label="Mensajes" value={stats.unread_contacts || 0} color="bg-pink-50 text-pink-600" />
        </div>

        <Tabs defaultValue="products">
          <TabsList className="bg-white border border-[var(--sage)]/15 mb-5 flex-wrap h-auto p-1 gap-0.5">
            <TabsTrigger value="dashboard" className="text-xs" data-testid="tab-dashboard"><BarChart3 className="h-3.5 w-3.5 mr-1" /> Dashboard</TabsTrigger>
            <TabsTrigger value="products" className="text-xs" data-testid="tab-products"><Package className="h-3.5 w-3.5 mr-1" /> Tienda</TabsTrigger>
            <TabsTrigger value="inventory" className="text-xs" data-testid="tab-inventory"><Warehouse className="h-3.5 w-3.5 mr-1" /> Inventario{(lowStockProducts.length + outOfStockProducts.length) > 0 && <Badge className="ml-1 bg-red-500 text-white border-0 text-[9px] px-1 py-0 h-4 min-w-4">{lowStockProducts.length + outOfStockProducts.length}</Badge>}</TabsTrigger>
            <TabsTrigger value="education" className="text-xs" data-testid="tab-education"><BookOpen className="h-3.5 w-3.5 mr-1" /> Skin Lab</TabsTrigger>
            <TabsTrigger value="blog" className="text-xs" data-testid="tab-blog"><FileText className="h-3.5 w-3.5 mr-1" /> Blog</TabsTrigger>
            <TabsTrigger value="coupons" className="text-xs" data-testid="tab-coupons"><Tag className="h-3.5 w-3.5 mr-1" /> Cupones</TabsTrigger>
            <TabsTrigger value="orders" className="text-xs" data-testid="tab-orders"><ShoppingBag className="h-3.5 w-3.5 mr-1" /> Ventas</TabsTrigger>
            <TabsTrigger value="design" className="text-xs" data-testid="tab-design"><Palette className="h-3.5 w-3.5 mr-1" /> Diseno</TabsTrigger>
            <TabsTrigger value="contacts" className="text-xs" data-testid="tab-contacts"><Mail className="h-3.5 w-3.5 mr-1" /> Mensajes</TabsTrigger>
          </TabsList>

          {/* ═══ Dashboard ═══ */}
          <TabsContent value="dashboard">
            <div className="grid lg:grid-cols-3 gap-5 mb-5">
              <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-[var(--sage)]/15">
                <h3 className="text-sm font-bold text-[var(--dark)] mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[var(--violet)]" /> Ventas por Mes</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%"><BarChart data={chartData.monthly || []}><CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => [`$${v}`, 'Ingresos']} /><Bar dataKey="revenue" fill="#9C6ADE" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-[var(--sage)]/15">
                <h3 className="text-sm font-bold text-[var(--dark)] mb-4 flex items-center gap-2"><Percent className="h-4 w-4 text-[var(--sage-dark)]" /> Por Categoria</h3>
                {categoryPieData.length > 0 ? (
                  <div className="h-52"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={categoryPieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>{categoryPieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer></div>
                ) : <div className="h-52 flex items-center justify-center text-xs text-[var(--dark)]/30">Sin datos</div>}
              </div>
            </div>

            {/* Top Products & Analytics */}
            <div className="grid lg:grid-cols-2 gap-5">
              <div className="bg-white rounded-2xl p-5 border border-[var(--sage)]/15" data-testid="top-products-panel">
                <h3 className="text-sm font-bold text-[var(--dark)] mb-4">Ranking: Productos Mas Vendidos</h3>
                {analytics?.top_products?.length > 0 ? (
                  <div className="space-y-2">
                    {analytics.top_products.map((tp, i) => (
                      <div key={i} className="flex items-center gap-3 py-2 border-b border-[var(--sage)]/5 last:border-0">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i < 3 ? 'bg-[var(--violet)] text-white' : 'bg-[var(--cream)] text-[var(--dark)]/40'}`}>{i + 1}</span>
                        <div className="flex-1 min-w-0"><p className="text-xs font-semibold text-[var(--dark)] truncate">{tp.name}</p><p className="text-[10px] text-[var(--dark)]/30">{tp.units} unidades</p></div>
                        <span className="text-sm font-bold text-[var(--violet)]">${tp.revenue?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-[var(--dark)]/30 py-8 text-center">Aun no hay ventas para mostrar ranking</p>}
              </div>
              <div className="bg-white rounded-2xl p-5 border border-[var(--sage)]/15" data-testid="analytics-panel">
                <h3 className="text-sm font-bold text-[var(--dark)] mb-4">Metricas</h3>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-[var(--cream)] rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-[var(--dark)]">{analytics?.total_page_views || 0}</p>
                    <p className="text-[10px] text-[var(--dark)]/40">Visitas Totales</p>
                  </div>
                  <div className="bg-[var(--cream)] rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-[var(--dark)]">{analytics?.today_page_views || 0}</p>
                    <p className="text-[10px] text-[var(--dark)]/40">Visitas Hoy</p>
                  </div>
                  <div className="bg-[var(--cream)] rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-[var(--dark)]">{analytics?.quiz_completions || 0}</p>
                    <p className="text-[10px] text-[var(--dark)]/40">Quiz Completados</p>
                  </div>
                  <div className="bg-[var(--cream)] rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-[var(--dark)]">{Object.values(analytics?.order_status_counts || {}).reduce((a, b) => a + b, 0)}</p>
                    <p className="text-[10px] text-[var(--dark)]/40">Pedidos Totales</p>
                  </div>
                </div>
                {/* Order status breakdown */}
                {analytics?.order_status_counts && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold text-[var(--dark)]/40 uppercase">Estado de Pedidos</p>
                    {Object.entries(analytics.order_status_counts).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <Badge className={`${STATUS_COLORS[status] || 'bg-gray-50 text-gray-600'} border-0 text-[10px]`}>{STATUS_LABELS[status] || status}</Badge>
                        <span className="text-xs font-bold text-[var(--dark)]">{count}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Google Analytics Prep */}
                <div className="mt-4 pt-4 border-t border-[var(--sage)]/10">
                  <p className="text-[10px] font-semibold text-[var(--dark)]/40 uppercase mb-2">Google Analytics</p>
                  <div className="flex gap-2">
                    <Input value={gaId} onChange={e => setGaId(e.target.value)} placeholder="ID: G-XXXXXXXXXX" className="h-8 text-xs flex-1" />
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => alert('Google Analytics configurado. En produccion, el tag se insertara automaticamente.')}>Guardar</Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ═══ Products (Tienda) ═══ */}
          <TabsContent value="products">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <div className="flex items-center gap-3 flex-1">
                <h2 className="text-base font-bold text-[var(--dark)]">Tienda ({filteredProducts.length})</h2>
                <Input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Buscar producto..." className="h-8 text-xs w-48" />
                <Select value={productFilterCat} onValueChange={setProductFilterCat}>
                  <SelectTrigger className="h-8 text-xs w-40"><SelectValue placeholder="Categoria" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todas</SelectItem>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button size="sm" className="bg-[var(--violet)] text-white text-xs" onClick={() => { setEditProduct(null); setShowProductForm(true); }} data-testid="add-product-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Nuevo Producto</Button>
            </div>

            <Dialog open={showProductForm} onOpenChange={v => { setShowProductForm(v); if (!v) setEditProduct(null); }}>
              <DialogContent className="max-w-2xl max-h-[90vh]">
                <DialogHeader><DialogTitle>{editProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle></DialogHeader>
                <ProductForm product={editProduct} onSave={saveProduct} onClose={() => { setShowProductForm(false); setEditProduct(null); }} token={token} />
              </DialogContent>
            </Dialog>

            <div className="grid gap-3">
              {filteredProducts.map(p => (
                <div key={p.id} className="bg-white rounded-xl p-4 flex items-center gap-4 border border-[var(--sage)]/15 hover:shadow-sm transition-shadow" data-testid={`admin-product-${p.id}`}>
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-[var(--cream)] flex-shrink-0">
                    <img src={p.images?.[0] || ''} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-[var(--dark)] truncate">{p.name}</p>
                      {p.featured && <Badge className="bg-yellow-50 text-yellow-700 border-0 text-[9px]">Destacado</Badge>}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px] capitalize">{p.category}</Badge>
                      <span className="text-[10px] text-[var(--dark)]/25">{p.brand}</span>
                      {p.images?.length > 1 && <span className="text-[10px] text-[var(--dark)]/25"><ImageIcon className="h-3 w-3 inline" /> {p.images.length}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-[var(--violet)]">${p.discount_price || p.price}</p>
                    {p.discount_price && <p className="text-[10px] text-[var(--dark)]/25 line-through">${p.price}</p>}
                  </div>
                  <Badge className={`text-[10px] border-0 flex-shrink-0 ${(p.stock || 0) <= 0 ? 'bg-red-50 text-red-600' : (p.stock || 0) <= (p.low_stock_threshold || 5) ? 'bg-orange-50 text-orange-600' : 'bg-green-50 text-green-600'}`}>
                    {(p.stock || 0) <= 0 ? 'Agotado' : `Stock: ${p.stock}`}
                  </Badge>
                  <Badge className={`text-[10px] border-0 flex-shrink-0 ${p.active ? 'bg-[var(--sage-light)] text-[var(--sage-dark)]' : 'bg-red-50 text-red-500'}`}>{p.active ? 'Activo' : 'Inactivo'}</Badge>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditProduct(p); setShowProductForm(true); }} data-testid={`edit-product-${p.id}`}><Pencil className="h-3.5 w-3.5 text-[var(--dark)]/40" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => deleteProduct(p.id)} disabled={deleteLoading === p.id} data-testid={`delete-product-${p.id}`}><Trash2 className={`h-3.5 w-3.5 ${deleteLoading === p.id ? 'text-gray-300' : 'text-red-400 hover:text-red-600'}`} /></Button>
                  </div>
                </div>
              ))}
              {filteredProducts.length === 0 && <p className="text-center py-10 text-xs text-[var(--dark)]/30">No hay productos en esta categoria</p>}
            </div>
          </TabsContent>

          {/* ═══ Inventory ═══ */}
          <TabsContent value="inventory" data-testid="inventory-tab-content">
            {/* Alerts */}
            {(lowStockProducts.length > 0 || outOfStockProducts.length > 0) && (
              <div className="mb-5 space-y-2">
                {outOfStockProducts.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3" data-testid="out-of-stock-alert">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-red-700">Productos Agotados ({outOfStockProducts.length})</p>
                      <p className="text-[10px] text-red-500">{outOfStockProducts.map(p => p.name).join(', ')}</p>
                    </div>
                  </div>
                )}
                {lowStockProducts.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3" data-testid="low-stock-alert">
                    <Bell className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-orange-700">Stock Bajo ({lowStockProducts.length})</p>
                      <p className="text-[10px] text-orange-500">{lowStockProducts.map(p => `${p.name} (${p.stock})`).join(', ')}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-[var(--dark)]">Control de Inventario</h2>
              <div className="flex gap-2">
                {[{v:'all',l:'Todos'},{v:'low',l:'Stock Bajo'},{v:'out',l:'Agotados'},{v:'ok',l:'En Stock'}].map(({v,l}) => (
                  <Button key={v} size="sm" variant={inventoryFilter === v ? 'default' : 'outline'} className={`text-xs h-8 ${inventoryFilter === v ? 'bg-[var(--violet)] text-white' : ''}`} onClick={() => setInventoryFilter(v)}>{l}</Button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[var(--sage)]/15 overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[var(--sage)]/10 bg-[var(--cream)]/50">
                  <th className="text-left py-3 px-4 text-[10px] font-semibold text-[var(--dark)]/40 uppercase">Producto</th>
                  <th className="text-center py-3 px-3 text-[10px] font-semibold text-[var(--dark)]/40 uppercase">Categoria</th>
                  <th className="text-center py-3 px-3 text-[10px] font-semibold text-[var(--dark)]/40 uppercase">Stock</th>
                  <th className="text-center py-3 px-3 text-[10px] font-semibold text-[var(--dark)]/40 uppercase">Umbral</th>
                  <th className="text-center py-3 px-3 text-[10px] font-semibold text-[var(--dark)]/40 uppercase">Estado</th>
                  <th className="text-center py-3 px-4 text-[10px] font-semibold text-[var(--dark)]/40 uppercase">Ajustar</th>
                </tr></thead>
                <tbody>
                  {inventoryProducts.map(p => {
                    const isLow = (p.stock || 0) <= (p.low_stock_threshold || 5) && (p.stock || 0) > 0;
                    const isOut = (p.stock || 0) <= 0;
                    return (
                      <tr key={p.id} className={`border-b border-[var(--sage)]/5 ${isOut ? 'bg-red-50/30' : isLow ? 'bg-orange-50/30' : ''}`} data-testid={`inv-row-${p.id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg overflow-hidden bg-[var(--cream)] flex-shrink-0"><img src={p.images?.[0] || ''} alt="" className="w-full h-full object-cover" /></div>
                            <p className="text-xs font-semibold text-[var(--dark)] truncate max-w-[180px]">{p.name}</p>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-center"><Badge variant="outline" className="text-[10px] capitalize">{p.category}</Badge></td>
                        <td className="py-3 px-3 text-center">
                          {stockEditId === p.id ? (
                            <Input value={stockEditVal} onChange={e => setStockEditVal(e.target.value)} type="number" className="h-7 w-16 text-xs text-center mx-auto" autoFocus onKeyDown={e => { if (e.key === 'Enter') updateStock(p.id, 'set', stockEditVal); if (e.key === 'Escape') setStockEditId(null); }} />
                          ) : (
                            <button onClick={() => { setStockEditId(p.id); setStockEditVal(String(p.stock || 0)); }} className="text-sm font-bold cursor-pointer hover:text-[var(--violet)] transition-colors" data-testid={`stock-val-${p.id}`}>{p.stock || 0}</button>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center text-xs text-[var(--dark)]/30">{p.low_stock_threshold || 5}</td>
                        <td className="py-3 px-3 text-center">
                          <Badge className={`text-[10px] border-0 ${isOut ? 'bg-red-100 text-red-700' : isLow ? 'bg-orange-100 text-orange-700' : 'bg-green-50 text-green-600'}`}>
                            {isOut ? 'Agotado' : isLow ? 'Bajo' : 'OK'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-500" onClick={() => updateStock(p.id, 'subtract', 1)} data-testid={`inv-minus-${p.id}`}><Minus className="h-3 w-3" /></Button>
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-green-600" onClick={() => updateStock(p.id, 'add', 1)} data-testid={`inv-plus-${p.id}`}><Plus className="h-3 w-3" /></Button>
                            {stockEditId === p.id && <Button size="sm" className="h-7 px-2 text-[10px] bg-[var(--violet)] text-white" onClick={() => updateStock(p.id, 'set', stockEditVal)}>OK</Button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {inventoryProducts.length === 0 && <p className="text-center py-10 text-xs text-[var(--dark)]/30">No hay productos en este filtro</p>}
            </div>
          </TabsContent>

          {/* ═══ Education / Skin Lab ═══ */}
          <TabsContent value="education" data-testid="education-tab-content">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-[var(--dark)]">Skin Lab ({education.length})</h2>
              <Button size="sm" className="bg-[var(--violet)] text-white text-xs" onClick={() => { setEditEdu(null); setEduForm({ title: '', slug: '', content: '', image: '', order: education.length + 1, published: false }); setShowEduForm(true); }} data-testid="add-education-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Nuevo Tutorial</Button>
            </div>
            {showEduForm && (
              <div className="bg-white rounded-2xl p-5 border border-[var(--sage)]/15 mb-5 space-y-3" data-testid="education-form">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Titulo</Label><Input value={eduForm.title} onChange={e => setEduForm({ ...eduForm, title: e.target.value })} className="h-9 text-sm" data-testid="edu-form-title" /></div>
                  <div className="space-y-1"><Label className="text-xs">Slug (URL)</Label><Input value={eduForm.slug} onChange={e => setEduForm({ ...eduForm, slug: e.target.value })} placeholder="auto-generado" className="h-9 text-sm" /></div>
                </div>
                <ImageManager images={eduForm.image ? [eduForm.image] : []} onChange={imgs => setEduForm({ ...eduForm, image: imgs[0] || '' })} token={token} single />
                <div className="space-y-1"><Label className="text-xs">Contenido</Label><Textarea value={eduForm.content} onChange={e => setEduForm({ ...eduForm, content: e.target.value })} className="text-sm min-h-[150px]" data-testid="edu-form-content" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Orden</Label><Input type="number" value={eduForm.order} onChange={e => setEduForm({ ...eduForm, order: e.target.value })} className="h-9 text-sm" /></div>
                  <div className="flex items-end pb-1 gap-2"><Switch checked={eduForm.published} onCheckedChange={v => setEduForm({ ...eduForm, published: v })} /><Label className="text-xs">Publicado</Label></div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={saveEducation} className="bg-[var(--violet)] text-white text-xs" data-testid="edu-form-save"><Save className="h-3.5 w-3.5 mr-1" /> {editEdu ? 'Actualizar' : 'Crear'}</Button>
                  <Button variant="outline" onClick={() => { setShowEduForm(false); setEditEdu(null); }} className="text-xs">Cancelar</Button>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {education.map(a => (
                <div key={a.id} className="bg-white rounded-xl p-4 flex items-center gap-4 border border-[var(--sage)]/15" data-testid={`edu-item-${a.id}`}>
                  {a.image && <div className="w-14 h-14 rounded-xl overflow-hidden bg-[var(--cream)] flex-shrink-0"><img src={a.image} alt="" className="w-full h-full object-cover" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[var(--dark)] truncate">{a.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px]">Orden: {a.order}</Badge>
                      <Badge className={a.published ? 'bg-[var(--sage-light)] text-[var(--sage-dark)] border-0 text-[10px]' : 'bg-yellow-50 text-yellow-700 border-0 text-[10px]'}>{a.published ? 'Publicado' : 'Borrador'}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditEdu(a); setEduForm(a); setShowEduForm(true); }} data-testid={`edit-edu-${a.id}`}><Pencil className="h-3.5 w-3.5 text-[var(--dark)]/40" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => deleteEducation(a.id)} disabled={deleteLoading === a.id} data-testid={`delete-edu-${a.id}`}><Trash2 className={`h-3.5 w-3.5 ${deleteLoading === a.id ? 'text-gray-300' : 'text-red-400'}`} /></Button>
                  </div>
                </div>
              ))}
              {education.length === 0 && <p className="text-center py-10 text-xs text-[var(--dark)]/30">No hay tutoriales.</p>}
            </div>
          </TabsContent>

          {/* ═══ Blog ═══ */}
          <TabsContent value="blog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-[var(--dark)]">Blog ({blogPosts.length})</h2>
              <Button size="sm" className="bg-[var(--violet)] text-white text-xs" onClick={() => { setEditBlog(null); setBlogForm({ title: '', slug: '', content: '', excerpt: '', image: '', category: '', published: false }); setShowBlogForm(true); }} data-testid="add-blog-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Nuevo Articulo</Button>
            </div>
            {showBlogForm && (
              <div className="bg-white rounded-2xl p-5 border border-[var(--sage)]/15 mb-5 space-y-3" data-testid="blog-form">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Titulo</Label><Input value={blogForm.title} onChange={e => setBlogForm({ ...blogForm, title: e.target.value })} className="h-9 text-sm" data-testid="blog-form-title" /></div>
                  <div className="space-y-1"><Label className="text-xs">Categoria</Label><Input value={blogForm.category} onChange={e => setBlogForm({ ...blogForm, category: e.target.value })} className="h-9 text-sm" /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Extracto</Label><Input value={blogForm.excerpt} onChange={e => setBlogForm({ ...blogForm, excerpt: e.target.value })} className="h-9 text-sm" /></div>
                <ImageManager images={blogForm.image ? [blogForm.image] : []} onChange={imgs => setBlogForm({ ...blogForm, image: imgs[0] || '' })} token={token} single />
                <div className="space-y-1"><Label className="text-xs">Contenido</Label><Textarea value={blogForm.content} onChange={e => setBlogForm({ ...blogForm, content: e.target.value })} className="text-sm min-h-[150px]" data-testid="blog-form-content" /></div>
                <div className="flex items-center gap-2"><Switch checked={blogForm.published} onCheckedChange={v => setBlogForm({ ...blogForm, published: v })} /><Label className="text-xs">Publicado</Label></div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={saveBlog} className="bg-[var(--violet)] text-white text-xs" data-testid="blog-form-save"><Save className="h-3.5 w-3.5 mr-1" /> {editBlog ? 'Actualizar' : 'Publicar'}</Button>
                  <Button variant="outline" onClick={() => { setShowBlogForm(false); setEditBlog(null); }} className="text-xs">Cancelar</Button>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {blogPosts.map(p => (
                <div key={p.id} className="bg-white rounded-xl p-4 flex items-center gap-4 border border-[var(--sage)]/15" data-testid={`blog-item-${p.id}`}>
                  {p.image && <div className="w-14 h-14 rounded-xl overflow-hidden bg-[var(--cream)] flex-shrink-0"><img src={p.image} alt="" className="w-full h-full object-cover" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[var(--dark)] truncate">{p.title}</p>
                    <div className="flex items-center gap-2 mt-1"><Badge variant="outline" className="text-[10px]">{p.category || 'General'}</Badge><Badge className={p.published ? 'bg-[var(--sage-light)] text-[var(--sage-dark)] border-0 text-[10px]' : 'bg-yellow-50 text-yellow-700 border-0 text-[10px]'}>{p.published ? 'Publicado' : 'Borrador'}</Badge></div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditBlog(p); setBlogForm(p); setShowBlogForm(true); }} data-testid={`edit-blog-${p.id}`}><Pencil className="h-3.5 w-3.5 text-[var(--dark)]/40" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => deleteBlog(p.id)} disabled={deleteLoading === p.id} data-testid={`delete-blog-${p.id}`}><Trash2 className={`h-3.5 w-3.5 ${deleteLoading === p.id ? 'text-gray-300' : 'text-red-400'}`} /></Button>
                  </div>
                </div>
              ))}
              {blogPosts.length === 0 && <p className="text-center py-10 text-xs text-[var(--dark)]/30">No hay articulos.</p>}
            </div>
          </TabsContent>

          {/* ═══ Coupons ═══ */}
          <TabsContent value="coupons">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-[var(--dark)]">Cupones ({coupons.length})</h2>
              <Button size="sm" className="bg-[var(--violet)] text-white text-xs" onClick={() => { setEditCoupon(null); setCouponForm({ code: '', discount_type: 'percentage', discount_value: 10, min_purchase: 0, max_uses: 100, active: true, expires_at: '' }); setShowCouponForm(true); }} data-testid="add-coupon-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Nuevo Cupon</Button>
            </div>
            {showCouponForm && (
              <div className="bg-white rounded-2xl p-5 border border-[var(--sage)]/15 mb-5 space-y-3">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Codigo</Label><Input value={couponForm.code} onChange={e => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} className="h-9 text-sm uppercase" data-testid="coupon-code-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">Tipo</Label><Select value={couponForm.discount_type} onValueChange={v => setCouponForm({ ...couponForm, discount_type: v })}><SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percentage">Porcentaje (%)</SelectItem><SelectItem value="fixed">Monto Fijo ($)</SelectItem></SelectContent></Select></div>
                  <div className="space-y-1"><Label className="text-xs">Valor</Label><Input type="number" value={couponForm.discount_value} onChange={e => setCouponForm({ ...couponForm, discount_value: e.target.value })} className="h-9 text-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Compra Min ($)</Label><Input type="number" value={couponForm.min_purchase} onChange={e => setCouponForm({ ...couponForm, min_purchase: e.target.value })} className="h-9 text-sm" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Usos Max</Label><Input type="number" value={couponForm.max_uses} onChange={e => setCouponForm({ ...couponForm, max_uses: e.target.value })} className="h-9 text-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Expira</Label><Input type="date" value={couponForm.expires_at?.split('T')[0] || ''} onChange={e => setCouponForm({ ...couponForm, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })} className="h-9 text-sm" /></div>
                  <div className="flex items-end pb-1 gap-2"><Switch checked={couponForm.active} onCheckedChange={v => setCouponForm({ ...couponForm, active: v })} /><Label className="text-xs">Activo</Label></div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={saveCoupon} className="bg-[var(--violet)] text-white text-xs" data-testid="coupon-save-btn">Guardar</Button>
                  <Button variant="outline" onClick={() => { setShowCouponForm(false); setEditCoupon(null); }} className="text-xs">Cancelar</Button>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {coupons.map(c => (
                <div key={c.id} className="bg-white rounded-xl p-4 flex items-center gap-4 border border-[var(--sage)]/15" data-testid={`coupon-${c.id}`}>
                  <div className="w-12 h-12 rounded-xl bg-[var(--violet-light)] flex items-center justify-center flex-shrink-0"><Tag className="h-5 w-5 text-[var(--violet)]" /></div>
                  <div className="flex-1"><p className="text-sm font-bold tracking-wider">{c.code}</p><p className="text-xs text-[var(--dark)]/40">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${c.discount_value}`} | Min: ${c.min_purchase} | Usos: {c.uses || 0}/{c.max_uses}</p></div>
                  <Badge className={c.active ? 'bg-green-50 text-green-600 border-0 text-xs' : 'bg-red-50 text-red-500 border-0 text-xs'}>{c.active ? 'Activo' : 'Inactivo'}</Badge>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditCoupon(c); setCouponForm(c); setShowCouponForm(true); }}><Pencil className="h-3.5 w-3.5 text-[var(--dark)]/40" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteCoupon(c.id)} disabled={deleteLoading === c.id}><Trash2 className={`h-3.5 w-3.5 ${deleteLoading === c.id ? 'text-gray-300' : 'text-red-400'}`} /></Button>
                  </div>
                </div>
              ))}
              {coupons.length === 0 && <p className="text-center py-10 text-xs text-[var(--dark)]/30">No hay cupones.</p>}
            </div>
          </TabsContent>

          {/* ═══ Orders (OMS) ═══ */}
          <TabsContent value="orders" data-testid="orders-tab-content">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <h2 className="text-base font-bold text-[var(--dark)]">Gestion de Pedidos ({filteredOrders.length})</h2>
              <div className="flex gap-2 flex-wrap">
                {[{v:'all',l:'Todos'},{v:'pending',l:'Pendiente'},{v:'paid',l:'Pagado'},{v:'shipped',l:'Enviado'},{v:'completed',l:'Completado'}].map(({v,l}) => (
                  <Button key={v} size="sm" variant={orderStatusFilter === v ? 'default' : 'outline'} className={`text-xs h-7 ${orderStatusFilter === v ? 'bg-[var(--violet)] text-white' : ''}`} onClick={() => setOrderStatusFilter(v)} data-testid={`order-filter-${v}`}>{l}</Button>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              {filteredOrders.map(o => (
                <div key={o.id} className="bg-white rounded-xl p-5 border border-[var(--sage)]/15" data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-xs font-bold text-[var(--dark)]">Pedido #{o.id?.slice(0, 8)}</span>
                      <span className="text-[10px] text-[var(--dark)]/30 ml-2">{new Date(o.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <Badge className={`${STATUS_COLORS[o.status] || 'bg-gray-50'} border-0 text-xs`} data-testid={`order-status-${o.id}`}>{STATUS_LABELS[o.status] || o.status}</Badge>
                  </div>

                  {/* Customer & Items */}
                  <div className="grid sm:grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-[10px] font-semibold text-[var(--dark)]/30 uppercase mb-1">Cliente</p>
                      <p className="text-xs text-[var(--dark)]">{o.user_email}</p>
                      {o.shipping_address && (
                        <p className="text-[10px] text-[var(--dark)]/40 mt-1">
                          {o.shipping_address.street}, {o.shipping_address.city} {o.shipping_address.zip}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-[var(--dark)]/30 uppercase mb-1">SKUs ({o.items?.length || 0})</p>
                      <div className="space-y-0.5">
                        {o.items?.map((item, i) => (
                          <p key={i} className="text-xs text-[var(--dark)]/60">{item.name} <span className="text-[var(--dark)]/30">x{item.quantity}</span> <span className="font-semibold text-[var(--dark)]">${(item.price * item.quantity).toFixed(2)}</span></p>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Total & Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-[var(--sage)]/10">
                    <div className="flex items-center gap-4">
                      {o.coupon_code && <Badge className="bg-purple-50 text-purple-600 border-0 text-[10px]">Cupon: {o.coupon_code}</Badge>}
                      {o.coupon_discount > 0 && <span className="text-[10px] text-green-600">-${o.coupon_discount?.toFixed(2)}</span>}
                      <span className="text-base font-bold text-[var(--dark)]">${o.total?.toFixed(2)}</span>
                    </div>
                    <div className="flex gap-2">
                      {STATUS_NEXT[o.status] && (
                        <Button size="sm" className="bg-[var(--violet)] text-white text-[10px] h-7" onClick={() => updateOrderStatus(o.id, STATUS_NEXT[o.status])} data-testid={`advance-order-${o.id}`}>
                          {STATUS_LABELS[STATUS_NEXT[o.status]]}
                        </Button>
                      )}
                      {o.status !== 'cancelled' && o.status !== 'completed' && (
                        <Button size="sm" variant="outline" className="text-red-500 border-red-200 text-[10px] h-7" onClick={() => updateOrderStatus(o.id, 'cancelled')} data-testid={`cancel-order-${o.id}`}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {filteredOrders.length === 0 && <p className="text-center py-10 text-xs text-[var(--dark)]/30">No hay pedidos</p>}
            </div>
          </TabsContent>

          {/* ═══ Design ═══ */}
          <TabsContent value="design" data-testid="design-tab-content">
            {designForm && (
              <div className="space-y-6">
                {/* Typography */}
                <div className="bg-white rounded-2xl p-6 border border-[var(--sage)]/15">
                  <h3 className="text-sm font-bold text-[var(--dark)] mb-1 flex items-center gap-2"><Type className="h-4 w-4 text-[var(--violet)]" /> Tipografia</h3>
                  <p className="text-[10px] text-[var(--dark)]/40 mb-4">Selecciona las fuentes para titulos y texto del cuerpo.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Fuente de Titulos</Label>
                      <Select value={designForm.fonts?.heading || 'Outfit'} onValueChange={v => setDesignForm({ ...designForm, fonts: { ...designForm.fonts, heading: v } })}>
                        <SelectTrigger className="h-9 text-sm" style={{ fontFamily: designForm.fonts?.heading || 'Outfit' }}><SelectValue /></SelectTrigger>
                        <SelectContent>{FONTS.map(f => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                      <p className="text-lg font-bold" style={{ fontFamily: designForm.fonts?.heading || 'Outfit' }}>Vista previa del titulo</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Fuente del Cuerpo</Label>
                      <Select value={designForm.fonts?.body || 'Outfit'} onValueChange={v => setDesignForm({ ...designForm, fonts: { ...designForm.fonts, body: v } })}>
                        <SelectTrigger className="h-9 text-sm" style={{ fontFamily: designForm.fonts?.body || 'Outfit' }}><SelectValue /></SelectTrigger>
                        <SelectContent>{FONTS.map(f => <SelectItem key={f} value={f} style={{ fontFamily: f }}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                      <p className="text-sm" style={{ fontFamily: designForm.fonts?.body || 'Outfit' }}>Vista previa del texto regular para la pagina.</p>
                    </div>
                  </div>
                </div>

                {/* Colors */}
                <div className="bg-white rounded-2xl p-6 border border-[var(--sage)]/15">
                  <h3 className="text-sm font-bold text-[var(--dark)] mb-1 flex items-center gap-2"><Palette className="h-4 w-4 text-[var(--violet)]" /> Paleta de Colores</h3>
                  <p className="text-[10px] text-[var(--dark)]/40 mb-4">Personaliza los colores de fondo, botones y textos de toda la web.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { key: 'violet', label: 'Botones / Principal' },
                      { key: 'violet_dark', label: 'Botones Hover' },
                      { key: 'violet_light', label: 'Fondo Acento' },
                      { key: 'sage', label: 'Verde Acento' },
                      { key: 'sage_dark', label: 'Verde Oscuro' },
                      { key: 'sage_light', label: 'Verde Claro' },
                      { key: 'dark', label: 'Texto Principal' },
                      { key: 'cream', label: 'Fondo General' },
                    ].map(({ key, label }) => (
                      <div key={key} className="space-y-1.5">
                        <Label className="text-[10px] text-[var(--dark)]/50">{label}</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={designForm.colors?.[key] || '#000000'} onChange={e => setDesignForm({ ...designForm, colors: { ...designForm.colors, [key]: e.target.value } })} className="w-8 h-8 rounded-lg border cursor-pointer" data-testid={`color-${key}`} />
                          <Input value={designForm.colors?.[key] || ''} onChange={e => setDesignForm({ ...designForm, colors: { ...designForm.colors, [key]: e.target.value } })} className="h-8 text-xs font-mono flex-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hero & Branding */}
                <div className="bg-white rounded-2xl p-6 border border-[var(--sage)]/15">
                  <h3 className="text-sm font-bold text-[var(--dark)] mb-1 flex items-center gap-2"><ImageIcon className="h-4 w-4 text-[var(--violet)]" /> Hero, Logo e Imagenes</h3>
                  <p className="text-[10px] text-[var(--dark)]/40 mb-4">Personaliza las imagenes y textos de la pagina principal.</p>
                  <div className="space-y-4">
                    <div className="space-y-1"><Label className="text-xs">Titulo Principal</Label><Input value={designForm.hero_title || ''} onChange={e => setDesignForm({ ...designForm, hero_title: e.target.value })} className="h-9 text-sm" data-testid="design-hero-title" /></div>
                    <div className="space-y-1"><Label className="text-xs">Subtitulo</Label><Textarea value={designForm.hero_subtitle || ''} onChange={e => setDesignForm({ ...designForm, hero_subtitle: e.target.value })} className="text-sm min-h-[60px]" /></div>
                    <div className="space-y-1"><Label className="text-xs">Texto Banner Superior</Label><Input value={designForm.banner_text || ''} onChange={e => setDesignForm({ ...designForm, banner_text: e.target.value })} className="h-9 text-sm" /></div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs mb-2 block">Imagen Hero</Label>
                        <ImageManager images={designForm.hero_image ? [designForm.hero_image] : []} onChange={imgs => setDesignForm({ ...designForm, hero_image: imgs[0] || '' })} token={token} single />
                      </div>
                      <div>
                        <Label className="text-xs mb-2 block">Logo</Label>
                        <ImageManager images={designForm.logo_url ? [designForm.logo_url] : []} onChange={imgs => setDesignForm({ ...designForm, logo_url: imgs[0] || '' })} token={token} single />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={saveDesign} disabled={designSaving} className="bg-[var(--violet)] hover:bg-[var(--violet-dark)] text-white px-8 h-11" data-testid="save-design-btn">
                    {designSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</> : <><Save className="h-4 w-4 mr-2" /> Guardar Diseno</>}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ═══ Contacts ═══ */}
          <TabsContent value="contacts">
            <h2 className="text-base font-bold text-[var(--dark)] mb-4">Mensajes ({contacts.length})</h2>
            <div className="space-y-3">
              {contacts.map(c => (
                <div key={c.id} className="bg-white rounded-xl p-4 border border-[var(--sage)]/15">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-xs text-[var(--dark)]">{c.name}</span>
                    <span className="text-[10px] text-[var(--dark)]/25">{new Date(c.created_at).toLocaleDateString('es-ES')}</span>
                  </div>
                  <p className="text-[10px] text-[var(--violet)] mb-1">{c.email}</p>
                  <p className="text-xs text-[var(--dark)]/50">{c.message}</p>
                </div>
              ))}
              {contacts.length === 0 && <p className="text-center py-10 text-xs text-[var(--dark)]/30">No hay mensajes</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
